import React from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import {useRouterStore} from "mobx-state-router";
import MediaFileSelectView from "../../elements/mediaFiles/MediaFileSelectView";
import {Button, Col, CustomInput, Row} from "reactstrap";
import {defaultStyles, FileIcon} from "react-file-icon";
import ViewFilePic from "../view/ViewFilePic";
import {FormattedMessage} from "react-intl";
import {
  getFileFormat,
  supportedImageFileTypes,
  supportedNonImageFileTypes,
  getCacheBustedUrl,
  isEpsFormat,
  resolveDownloadFilename
} from "../../../utils/common";
import Notification from "../../../utils/Notification";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faTrash} from "@fortawesome/free-solid-svg-icons";
import {convertByteToMB} from "../../../utils/common";
import {fileSizeConstants, imageFileTypes} from "../../../utils/constants";
import {observer} from "mobx-react-lite";

// Allowed image formats for logo and background fields.
// Uses imageFileTypes constants where available; jpg and pdf are added separately
// since they are not defined in imageFileTypes.
const logoAndBackgroundFormats = [
  imageFileTypes.PNG,                   // "png"
  imageFileTypes.JPEG, 'jpg',           // "jpeg" + "jpg" extension alias
  ...imageFileTypes.TIFF,               // ["tiff", "tif"]
  imageFileTypes.EPS[0],                // "eps" (exclude "postscript" alias)
  'pdf',
];

const MediaFileCodeUploadWidget = observer(({id, onChange, value, options = {}, currentState, currentMediaFileSelect = null, groupSelectStore, parentStore, eyesAndModulesStore}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const experienceFieldName = id?.replace('root_', '')
  const routerStore = useRouterStore();

  // React state for independent switch button management
  const [isCodeSpecific, setIsCodeSpecific] = React.useState(true);

  // Get the viewMode from options (provided through uiSchema) to determine what state to use
  const viewMode = options.viewMode || 'unknown';
  const {createCodeViewState, editCodeState} = routerStore.options.rootStore.rootUiStore;
  const state = viewMode === 'createCodeView' ? createCodeViewState : viewMode === 'editCodeView' ? editCodeState : currentState;
  // Derive mediaFileSelectState directly from MobX state (observer pattern)
  const mediaFileSelectState = (() => {
    if(currentMediaFileSelect){
      return currentMediaFileSelect;
    } else {
      // Get the field-specific MediaFileSelectState using the manager
      const mediaFileManager = routerStore.options.rootStore.rootUiStore.mediaFileSelectStateManager;

      if (mediaFileManager && state?.experienceSelected) {
        // Only try to get state if an experience is selected and manager should be initialized
        const selectedState = mediaFileManager.getStateForField(experienceFieldName);
        if (selectedState) {
          return selectedState;
        }
      }

      // Fallback to default if manager doesn't find field-specific state
      if (mediaFileManager) {
        console.warn(`No state found for field ${experienceFieldName}, manager has ${mediaFileManager.stateCount} states:`, mediaFileManager.fieldNames);
      }
      return routerStore.options.rootStore.rootUiStore.mediaFileSelectState;
    }
  })();

  // useEffect to sync React state with MobX when files are selected
  React.useEffect(() => {
    if (currentMediaFileSelect) {
      if (experienceFieldName === "mediaFileCode_logo" && currentState?.logoMediaFile) {
        setIsCodeSpecific(currentState.logoMediaFile.singleUseFile ?? true);
      } else if (experienceFieldName === "mediaFileCode_background" && currentState?.backgroundMediaFile) {
        setIsCodeSpecific(currentState.backgroundMediaFile.singleUseFile ?? true);
      }
    } else if (mediaFileSelectState?.selected) {
      setIsCodeSpecific(mediaFileSelectState.selected.singleUseFile ?? true);
    }
  }, [
    currentState?.logoMediaFile?.singleUseFile,
    currentState?.backgroundMediaFile?.singleUseFile,
    mediaFileSelectState?.selected?.singleUseFile,
    experienceFieldName,
    currentMediaFileSelect
  ]);

  // const handleMediaFileSelection = (selectedMediaFile) => {
  //   if (selectedMediaFile && selectedMediaFile.liveLinks) {
  //     // Trigger onChange with the selected file's thumbnail URL for display
  //     if (onChange) {
  //       onChange(selectedMediaFile.liveLinks.thumbnail || selectedMediaFile.liveLinks.original);
  //     }
  //   }
  // };


  const codeSpecificId = id?.replace('root_', '') + "_codeSpecific"
  // const [isExternalUrl, setIsExternalUrl] = useState(false);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const hiddenFileInput = React.useRef(null);
  const handleFileUploadClick = () => {
    hiddenFileInput.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (
      (viewMode === "createCodeView" || viewMode === "editCodeView") &&
      file.size > fileSizeConstants.MAX_UPLOAD_CONTENT_FILE
    ) {
      new Notification()
        .setType("error")
        .setMessage(`File size: ${convertByteToMB(file.size)} MB is larger than 200 MB upload limit.`)
        .send();
      return;
    }
    if (file && (supportedImageFileTypes(getFileFormat(file))||supportedNonImageFileTypes(getFileFormat(file)))) {
      if(currentMediaFileSelect) { // if this is for the designer template
        if(experienceFieldName === "mediaFileCode_logo") {
          const isValid = await parentStore.setSelectedImage(file, true, eyesAndModulesStore)
          if(!isValid) return
        } else if(experienceFieldName === "mediaFileCode_background") {
          const isValid = await parentStore.setSelectedImage(file, false, eyesAndModulesStore)
          if(!isValid) return
        }
        currentState.setFileForS3Upload(file, isCodeSpecific)
        currentState.createMediaFileFromWidget(experienceFieldName, mediaFileSelectState, groupSelectStore)
      } else {
        await state.setFileForS3Upload(file, isCodeSpecific);
        const newMediaFile = await state.createMediaFileFromWidget(experienceFieldName, mediaFileSelectState, state);

        state.addExperienceFieldAndMediaFileUuid(newMediaFile?.uuid, experienceFieldName)
        // onChange will be called by handleMediaFileSelection when upload completes
        // onChange(mediaFileSelectState?.selected?.liveLinks?.original);
      }
    } else {
      new Notification()
        .setType("error")
        .setMessage(`File type ${getFileFormat(file)} is not supported at this time`)
        .send();
    }
  };
  let fileExtension
  // if it is a designer template, then the file format is in the currentState
  if(currentMediaFileSelect) {
    fileExtension = currentState.fileFormat
  } else {
    fileExtension = mediaFileSelectState?.selected?.fileFormat
  }
  const removeTimestampSuffix = (nameWithTimestamp) => {
    if (mediaFileSelectState?.selected?.singleUseFile === false) {
      return nameWithTimestamp
    }
    return nameWithTimestamp?.replace(/_(\d+)(\.[a-zA-Z0-9]+)$/, '$2');
  }
  const downloadMedia = async (fileUrl, filename) => {
    if (!fileUrl) return;
    try {
      const response = await axios.get(fileUrl, { responseType: 'blob' });
      saveAs(response.data, filename);
    } catch (e) {
      new Notification()
        .setType('error')
        .setMessage(`Failed to download ${filename}`)
        .send();
    }
  };

  value = getCacheBustedUrl(value)
  return (
    <>
      {/* <Row style={{marginTop: "15px", marginBottom: "15px"}}> */}
      {/*   <Col sm={3} className="text-right">Use external URL</Col> */}
      {/*   <Col sm={1} className="text-right"> */}
      {/*     <CustomInput */}
      {/*       type="switch" */}
      {/*       id="externalUrl" */}
      {/*       checked={isExternalUrl} */}
      {/*       onChange={() => { */}
      {/*         setIsExternalUrl(!isExternalUrl); */}
      {/*       }} */}
      {/*     /> */}
      {/*   </Col> */}

        {/* {isExternalUrl && */}
        {/*   <> */}
        {/*     <Col sm={3}> */}
        {/*       <input */}
        {/*         type="text" */}
        {/*         value={value} */}
        {/*         onChange={(e) => { */}
        {/*           onChange(e.target.value); */}
        {/*         } */}
        {/*         } */}
        {/*       /> */}
        {/*     </Col> */}
        {/*   </> */}
        {/* } */}
      {/* </Row> */}
      {(!currentMediaFileSelect || (currentMediaFileSelect && (experienceFieldName === "mediaFileCode_logo" || experienceFieldName === "mediaFileCode_background"))) &&
        <>
          {/* {!isExternalUrl && */}
            <Row className="media-file-row-spacing">
              <Col sm={3} className="text-right"><FormattedMessage id="mediaFile.mediaWidgetForm.uploadOnly.defaultMessage"/></Col>
              <Col sm={1} className="text-right">
                <CustomInput
                  type="switch"
                  id={codeSpecificId}
                  checked={!isCodeSpecific}
                  onChange={() => {
                    const newValue = !isCodeSpecific;
                    setIsCodeSpecific(newValue);

                    if (currentMediaFileSelect) {
                      // For designer templates, update the appropriate MediaFile's singleUseFile property
                      if (experienceFieldName === "mediaFileCode_logo" && currentState.logoMediaFile) {
                        currentState.logoMediaFile.singleUseFile = newValue;
                      } else if (experienceFieldName === "mediaFileCode_background" && currentState.backgroundMediaFile) {
                        currentState.backgroundMediaFile.singleUseFile = newValue;
                      }
                    } else {
                      // For regular media files, update field-specific state when available
                      if (mediaFileSelectState?.selected) {
                        mediaFileSelectState.selected.setSingleUseFile(newValue);
                      }
                    }
                  }}
                />
              </Col>
              <Col sm={3} className="text-left"><FormattedMessage id="mediaFile.mediaWidgetForm.saveOrUseMediaFile.defaultMessage"/></Col>
            </Row>
        </>
      }

      <Row className="media-file-row-spacing">
        <Col sm={3}>
          <Button id="upload-btn" onClick={handleFileUploadClick}>
            {(!isCodeSpecific && (!currentMediaFileSelect || (currentMediaFileSelect && (experienceFieldName === "mediaFileCode_logo" || experienceFieldName === "mediaFileCode_background")))) ?
              <FormattedMessage id="mediaFile.mediaWidgetForm.uploadAndAddToList.defaultMessage"/> :
              "Upload"
            }
          </Button>
          <input
            type="file"
            style={{display: "none"}}
            ref={hiddenFileInput}
            onChange={handleFileChange}
          ></input>
        </Col>
        {((!isCodeSpecific || currentMediaFileSelect?.isMediaFileEnabled) && mediaFileSelectState?.shouldRenderSelect) &&
        <>
            <Col sm={3} className="text-right">Select from list</Col>
            <Col sm={6}>
              <MediaFileSelectView
                store={mediaFileSelectState}
                experienceFieldName={experienceFieldName}
                // Logo and background only support image formats
                filterFormats={
                  (experienceFieldName === 'mediaFileCode_logo' || experienceFieldName === 'mediaFileCode_background')
                    ? logoAndBackgroundFormats
                    : undefined
                }
                // notify={handleMediaFileSelection}
              />
            </Col>
        </>
      }
      </Row>


      {/* {(!isExternalUrl) && */}
        <Row>
          {(value) && (
            <>
              <Col className="text-right media-file-info-text">
                {(() => {
                  const rawName = currentMediaFileSelect
                    ? currentState?.name
                    : mediaFileSelectState?.selected?.name;
                  const fileName = rawName ? removeTimestampSuffix(rawName) : "";
                  if (!fileName) return "";
                  const originalUrl = currentMediaFileSelect
                    ? currentState?.liveLinks?.original
                    : mediaFileSelectState?.selected?.liveLinks?.original;
                  const downloadUrl = originalUrl || value;
                  const downloadName = resolveDownloadFilename(fileName, downloadUrl);
                  return (
                    <a
                      href="#"
                      className="media-file-name-link"
                      onClick={(e) => { e.preventDefault(); downloadMedia(downloadUrl, downloadName); }}
                    >
                      {fileName}
                    </a>
                  );
                })()}
              </Col>
              <Col>
                {(fileExtension) &&
                  <>
                    {supportedImageFileTypes(fileExtension) && !isEpsFormat(fileExtension) ?
                      <div className="media-file-preview-clickable" onClick={async () => {
                        await state?.handleFilePicState(value)
                        state.handleConfigModalPopUp(ViewFilePic, `${mediaFileSelectState?.selected?.name}`, "viewFilePicState")
                      }
                      }>
                        <img src={value} alt={"url"} className="media-file-preview-image"/>
                      </div> :
                      <div className="media-file-icon-container"><FileIcon extension={fileExtension} {...defaultStyles[fileExtension]} />
                      </div>
                    }
                    {currentMediaFileSelect &&
                      <div>
                      <FontAwesomeIcon onClick={() => {
                          // Use the enhanced LogoState removal methods that also clear the select dropdown
                          if (experienceFieldName === "mediaFileCode_logo" && parentStore?.removeLogoMediaFile) {
                            parentStore.removeLogoMediaFile();
                          } else if (experienceFieldName === "mediaFileCode_background" && parentStore?.removeBackgroundMediaFile) {
                            parentStore.removeBackgroundMediaFile();
                          } else {
                            // Fallback to the original method for non-designer contexts
                            state.removeMediaFile(id);
                          }
                        }} className="media-file-delete-icon align-middle" icon={faTrash} />
                      </div>
                    }
                  </>
                }
                {/*{renderFilePic(value, createCodeViewState ? createCodeViewState : editCodeState, mediaFileSelectState)}*/}
              </Col>
            </>
          )}
        </Row>
      {/* } */}
      {/*{*/}
      {/*  (!isExternalUrl) &&*/}
      {/*  <>*/}
      {/*    {(value) && (*/}
      {/*      <p>File Url: {value}</p>*/}
      {/*    )}*/}
      {/*    <div>Accepted formats: audio/video (mp3, mp4, wav), documents (doc, docx, xls, xlsx, csv, txt, ppt, pptx,*/}
      {/*      pdf),*/}
      {/*      images (jpg, jpeg, png, gif, tiff, svg, eps, pdf), others ( JSON, HTML, XML)*/}
      {/*    </div>*/}
      {/*  </>*/}
      {/*}*/}
    </>
  );
});

export default MediaFileCodeUploadWidget;
